import React, { FC } from 'react'
import { RegistrationType } from '../../../lib/registration'
import { useSiteMetadata } from '../../../lib/sanity/hooks/useSiteMetadata'
import * as Styled from './styled'

export interface RegistrationTypeSelectorProps {
  allowPractitioner: boolean
  allowPLPractitioner: boolean
  allowPLUnlicensed: boolean
  allowPatient: boolean
  allowStudent: boolean
  disabled: boolean
  selectedType: RegistrationType | null
  selectType: (type: RegistrationType) => void
}

export const RegistrationTypeSelector: FC<RegistrationTypeSelectorProps> = ({
  allowPractitioner,
  allowPLPractitioner,
  allowPLUnlicensed,
  allowPatient,
  allowStudent,
  disabled,
  selectedType,
  selectType,
}) => {
  const { featureFlags } = useSiteMetadata() || {}
  const allowPatientZipCodeRegistration = featureFlags?.allowPatientZipCodeRegistration ?? false
  return (
    <Styled.Container>
      <Styled.Title>Select your account type</Styled.Title>
      <Styled.ButtonGroup>
        {allowPractitioner && (
          <Styled.ButtonContainer>
            <Styled.Button
              type="button"
              disabled={disabled}
              active={selectedType === 'practitioner'}
              onClick={() => selectType('practitioner')}
            >
              I am a <Styled.TypeName>Practitioner</Styled.TypeName>
            </Styled.Button>
            <Styled.Helper>We will need to validate your license</Styled.Helper>
          </Styled.ButtonContainer>
        )}
        {allowPLPractitioner && (
          <Styled.ButtonContainer>
            <Styled.Button
              type="button"
              disabled={disabled}
              active={selectedType === 'private-label-practitioner'}
              onClick={() => selectType('private-label-practitioner')}
            >
              I am a licensed <Styled.TypeName>Practitioner</Styled.TypeName>
            </Styled.Button>
            <Styled.Helper>We will need to validate your license</Styled.Helper>
          </Styled.ButtonContainer>
        )}
        {allowPLUnlicensed && (
          <Styled.ButtonContainer>
            <Styled.Button
              type="button"
              disabled={disabled}
              active={selectedType === 'private-label-unlicensed'}
              onClick={() => selectType('private-label-unlicensed')}
            >
              I am <b>not</b> a licensed <Styled.TypeName>Practitioner</Styled.TypeName>
            </Styled.Button>
            <Styled.Helper>We will be in touch</Styled.Helper>
          </Styled.ButtonContainer>
        )}
        {allowPatient && (
          <Styled.ButtonContainer>
            <Styled.Button
              type="button"
              disabled={disabled}
              active={selectedType === 'patient' || selectedType === 'patient-zip-code'}
              onClick={() => selectType('patient-zip-code')}
            >
              I am a <Styled.TypeName>Patient</Styled.TypeName>
            </Styled.Button>
            <Styled.Helper>
              {allowPatientZipCodeRegistration
                ? 'We will need your zip code or practitioner code'
                : 'We will need your practitioner code'}
            </Styled.Helper>
          </Styled.ButtonContainer>
        )}
        {allowStudent && (
          <Styled.ButtonContainer>
            <Styled.Button
              type="button"
              disabled={disabled}
              active={selectedType === 'student'}
              onClick={() => selectType('student')}
            >
              I am a <Styled.TypeName>Student</Styled.TypeName>
            </Styled.Button>
            <Styled.Helper>We will need to verify your enrollment</Styled.Helper>
          </Styled.ButtonContainer>
        )}
      </Styled.ButtonGroup>
    </Styled.Container>
  )
}
